import type { Machine } from '@bridgewerk/shared';

const CONTROLLER_LABEL: Record<string, string> = {
  fanuc_0i_md: 'Fanuc 0i-MD',
  fanuc_30i: 'Fanuc 30i',
  mazak_matrix: 'Mazak Matrix',
  mazak_smoothx: 'Mazak SmoothX',
  haas_nextgen: 'Haas NextGen',
  mori_mapps: 'Mori MAPPS',
  okuma_osp: 'Okuma OSP',
  siemens_840d: 'Siemens 840D',
  heidenhain_tnc: 'Heidenhain TNC',
  unknown: 'Unknown',
};

export interface MachineInfoCardProps {
  readonly machine: Machine;
}

export function MachineInfoCard({ machine }: MachineInfoCardProps): JSX.Element {
  return (
    <section className="rounded-md border border-border bg-card p-5">
      <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim mb-3">
        Machine info
      </h2>
      <dl className="space-y-2 text-sm">
        <Row label="Controller">
          {CONTROLLER_LABEL[machine.controller_type] ?? machine.controller_type}
        </Row>
        <Row label="Machine ID">
          <span className="font-mono">{machine.machine_id}</span>
        </Row>
        <Row label="Shop">
          <span className="font-mono">{machine.shop_id}</span>
        </Row>
        <Row label="Last seen">{formatRelativeTime(machine.last_seen_at)}</Row>
        <Row label="Updated">{formatRelativeTime(machine.updated_at)}</Row>
      </dl>
      <p className="mt-3 text-[10px] text-muted">
        Serial, baud rate, and firmware version land in v1.1 once the dongle
        registry is wired up.
      </p>
    </section>
  );
}

function Row({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-dim">
        {label}
      </dt>
      <dd className="text-text">{children}</dd>
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
